<script setup lang="ts">
import { useInitials, useSignature, useTypography } from '@/composable'
import { useDocumentStore } from '@/store/document'
import { CheckIcon } from '@gohighlevel/ghl-icons/24/outline'
import {
  UIModal,
  UIModalContent,
  UIModalFooter,
  UIModalHeader,
  UISelect,
  UITab,
  UITabs,
  UITextXsRegular,
  UITextSmNormal,
} from '@gohighlevel/ghl-ui'
import { computed } from 'vue'
import { VueSignaturePad } from 'vue-signature-pad'
import { useI18n } from 'vue-i18n'
import { onMounted } from 'vue'

const { t } = useI18n()

const props = defineProps({
  id: {
    type: String,
    required: true,
  },
  show: {
    type: Boolean,
  },
  signerName: {
    type: String,
    required: true,
  },
  alignToCenter: {
    type: Boolean,
    default: false,
  },
  forInitials: {
    type: Boolean,
    default: false,
  },
  height: {
    type: Number,
    default: 300,
  },
  width: {
    type: Number,
    default: 500,
  },
})

const emit = defineEmits([
  'update:show',
  'update:signatureText',
  'update:signatureTextStr',
])
const store = useDocumentStore()
const { toCapitalize } = useTypography()
const businessName = computed(() => store.document.businessName)
const {
  signatureText,
  getImg,
  selectedColor,
  selectedFontFamily,
  activeTab,
  fonts,
  colors,
  signaturePad,
  errorMessage,
  resetError,
} = props.forInitials ? useInitials() : useSignature()

// Update to use the new flags
const enableDocumentSignatureDraw = computed(
  () => (store.document as any).enableDocumentSignatureDraw ?? true
)
const enableDocumentSignatureType = computed(
  () => (store.document as any).enableDocumentSignatureType ?? true
)

// Filter available tabs based on settings
const availableTabs = computed(() => {
  // For initials, always show both options
  if (props.forInitials) {
    return ['Draw', 'Type']
  }

  // For signatures, respect the settings
  const tabs = []
  if (enableDocumentSignatureDraw.value) tabs.push('Draw')
  if (enableDocumentSignatureType.value) tabs.push('Type')
  // If neither is enabled, enable both for backward compatibility
  if (tabs.length === 0) return ['Draw', 'Type']
  return tabs
})

// Set default active tab based on available options
onMounted(() => {
  if (props.signerName) {
    signatureText.value = props.signerName
  }

  // Set initial active tab based on enabled options
  if (availableTabs.value.length === 1) {
    activeTab.value = availableTabs.value[0]
  } else if (enableDocumentSignatureType.value || props.forInitials) {
    activeTab.value = 'Type'
  }
})

const onChange = (e: Event) => {
  resetError()
  const value = (e.target as HTMLInputElement).value
  signatureText.value = value
}

const submitCallback = async () => {
  const signedText = signatureText.value || props.signerName
  const dataImg = await getImg({
    width: props.width,
    height: props.height,
    text: signedText,
  })
  if (errorMessage.value) return
  emit('update:signatureText', dataImg)
  emit('update:show', false)
  emit('update:signatureTextStr', signedText)
}

function cancelCallback() {
  emit('update:show', false)
}

const options = computed(() => {
  return fonts.map(font => {
    return {
      label: font.replace(/'/g, ''),
      value: font,
    }
  })
})

const inputStyle = computed(() => {
  return {
    fontFamily: selectedFontFamily.value,
    color: selectedColor.value,
    fontSize: '5em',
    'object-fit': 'contain',
    'object-position': 'center',
    display: 'block',
    margin: 'auto',
  }
})

const modalDescription = computed(() => {
  const type = props.forInitials ? 'initial' : 'signature'
  if (availableTabs.value.length === 1) {
    return availableTabs.value[0] === 'Draw'
      ? t('documentViewer.addAHandDrawnOnly', { type })
      : t('documentViewer.addADigitalOnly', { type })
  }
  return `${t('documentViewer.addADigital')} ${type} ${
    availableTabs.value.length > 1 ? t('documentViewer.orAHandDrawn') : ''
  }`
})
</script>

<template>
  <UIModal
    :show="show"
    @update:show="val => $emit('update:show', val)"
    :id="'signature_modal' + props.id"
    :key="'signature_modal' + props.id"
    :width="800"
    class-name="page:!w-screen page:h-screen page:!fixed page:!top-0 page:!left-0 page:!z-[500] page:p-4 page:rounded-none"
  >
    <template #header>
      <UIModalHeader
        id="modal-header"
        type="success"
        :title="
          props.forInitials
            ? t('documentViewer.addInitial')
            : t('documentViewer.addSignature')
        "
        :description="modalDescription"
        @close="cancelCallback"
      />
    </template>
    <!--  content goes here  -->
    <UIModalContent>
      <UITabs
        v-if="availableTabs.length > 1"
        ref="tabsInstRef"
        v-model:value="activeTab"
        type="line"
      >
        <UITab v-for="tab in availableTabs" :key="tab" :name="tab">
          {{ t(`documentViewer.${tab.toLowerCase()}`) }}
        </UITab>
      </UITabs>
      <div class="py-4">
        <div
          class="border rounded-lg"
          v-if="
            activeTab === 'Draw' &&
            (props.forInitials || enableDocumentSignatureDraw)
          "
        >
          <VueSignaturePad
            height="300px"
            ref="signaturePad"
            :options="{ onBegin: resetError }"
          />
        </div>
        <div
          v-if="
            activeTab === 'Type' &&
            (props.forInitials || enableDocumentSignatureType)
          "
          class="border rounded-lg"
        >
          <div class="signature-container p-3 flex flex-col justify-between">
            <div class="flex justify-between">
              <UISelect
                class="w-64"
                id="select-id"
                v-model:value="selectedFontFamily"
                :options="options"
                :placeholder="t('documentViewer.selectFontPlaceholder')"
              />
              <div class="flex-1">
                <div class="flex items-center justify-end space-x-3">
                  <div v-for="(color, index) in colors" :key="index">
                    <div
                      @click="selectedColor = color"
                      :class="[
                        selectedColor === color
                          ? 'border-2 border-yellow-400 w-8 h-8'
                          : 'w-6 h-6',
                      ]"
                      :style="{ backgroundColor: color }"
                      class="rounded-full flex justify-center items-center"
                    >
                      <CheckIcon
                        v-if="selectedColor === color"
                        class="w-4 h-4 text-white"
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <div v-if="errorMessage">
              <UITextSmNormal class="text-error-500 m-0">{{
                errorMessage
              }}</UITextSmNormal>
            </div>
            <div class="w-full overflow-auto">
              <input
                :aria-label="t('documentViewer.signatureInputAriaLabel')"
                :style="inputStyle"
                type="text"
                class="signature-input text-center"
                :value="signatureText"
                @change="onChange"
              />
              <canvas
                id="canvas"
                class="hidden"
                :width="props.width"
                :height="props.height"
              />
            </div>
          </div>
        </div>
        <div class="p-4">
          <UITextXsRegular>
            {{
              t('documentViewer.signatureDisclaimer', {
                businessName: toCapitalize(businessName),
              })
            }}
          </UITextXsRegular>
        </div>
      </div>
    </UIModalContent>

    <template #footer>
      <UIModalFooter
        :positive-text="t('documentViewer.acceptAndSign')"
        :negative-text="t('documentViewer.cancel')"
        @positive-click="submitCallback"
        @negative-click="cancelCallback"
        id="modal-footer"
        class="page:fixed page:w-screen page:p-4 page:bottom-0 page:left-0 page:box-border"
      />
    </template>
  </UIModal>
</template>

<style lang="scss">
@import url('https://fonts.googleapis.com/css2?family=Aguafina+Script&family=Dancing+Script&family=Style+Script&family=Vujahday+Script&display=swap');

.signature-container {
  height: 275px;
}
.signature-input {
  width: 100%;
  font-size: 40px;
  height: auto;
  text-align: center;
  appearance: none;
  border-radius: 0px;
  border-bottom: 1px solid #000;
  outline: none;
}
</style>
